import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchasesAPI, productsAPI, suppliersAPI, warehousesAPI } from '../../services/api';
import BarcodeScanner from '../common/BarcodeScanner';
import toast from 'react-hot-toast';

export default function PurchaseOrderForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    suppliersAPI.list({ limit: 200 }).then(r => setSuppliers(r.data.suppliers)).catch(() => {});
    warehousesAPI.list().then(r => { setWarehouses(r.data); const def = r.data.find(w => w.is_default); if (def) setWarehouseId(def.id); }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (productSearch.length < 2) return setProductResults([]);
      try { const r = await productsAPI.list({ search: productSearch, limit: 10 }); setProductResults(r.data.products); setShowSearch(true); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [productSearch]);

  const addProduct = (p) => {
    setShowSearch(false); setProductSearch('');
    setItems(prev => [...prev, { product_id: p.id, product_name: p.name, sku: p.sku, hsn_code: p.hsn_code, quantity: 1, unit_price: Number(p.purchase_price) || 0, discount_pct: 0, gst_rate: Number(p.gst_rate) || 12 }]);
  };

  const updateItem = (idx, field, value) => setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleBarcodeScan = async (barcode) => {
    setShowScanner(false);
    try {
      const res = await productsAPI.lookupBarcode(barcode);
      if (res.data) { toast.success(`Found: ${res.data.name}`); addProduct(res.data); return; }
    } catch {}
    try {
      const r = await productsAPI.list({ search: barcode, limit: 1 });
      if (r.data.products.length > 0) { toast.success(`Found: ${r.data.products[0].name}`); addProduct(r.data.products[0]); }
      else toast.error(`No product for barcode: ${barcode}`);
    } catch { toast.error(`Not found: ${barcode}`); }
  };

  const total = items.reduce((sum, i) => {
    const taxable = i.quantity * i.unit_price * (1 - i.discount_pct / 100);
    return sum + taxable + (taxable * i.gst_rate / 100);
  }, 0);

  const handleSubmit = async () => {
    if (!supplierId) return toast.error('Select a supplier');
    if (!warehouseId) return toast.error('Select a warehouse');
    if (items.length === 0) return toast.error('Add at least one product');
    setLoading(true);
    try {
      const res = await purchasesAPI.createOrder({ supplier_id: supplierId, warehouse_id: warehouseId, expected_delivery: expectedDelivery || null, notes, items: items.map(i => ({ product_id: i.product_id, quantity: Number(i.quantity), unit_price: Number(i.unit_price), discount_pct: Number(i.discount_pct), gst_rate: Number(i.gst_rate) })) });
      toast.success(`PO created: ${res.data.po_number}`);
      navigate('/purchases/orders');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="page-header"><h1>New Purchase Order</h1><button onClick={() => navigate('/purchases/orders')} className="btn btn-secondary">Cancel</button></div>
      <div className="card mb-4"><div className="card-header"><h2>Order Details</h2></div><div className="card-body">
        <div className="form-row form-row-3">
          <div className="form-group"><label className="form-label">Supplier *</label><select className="form-select" value={supplierId} onChange={e => setSupplierId(e.target.value)}><option value="">Select Supplier</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name} {s.gstin ? `(${s.gstin})` : ''}</option>)}</select></div>
          <div className="form-group"><label className="form-label">Warehouse *</label><select className="form-select" value={warehouseId} onChange={e => setWarehouseId(e.target.value)}><option value="">Select</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
          <div className="form-group"><label className="form-label">Expected Delivery</label><input type="date" className="form-input" value={expectedDelivery} onChange={e => setExpectedDelivery(e.target.value)} /></div>
        </div>
      </div></div>
      <div className="card mb-4"><div className="card-header"><h2>Items</h2></div><div className="card-body">
        <div style={{ display:'flex', gap:10, marginBottom:16 }}>
          <div style={{ position:'relative', flex:1 }}>
            <input className="form-input" placeholder="Search products..." value={productSearch} onChange={e => setProductSearch(e.target.value)} onFocus={() => productResults.length > 0 && setShowSearch(true)} />
            {showSearch && productResults.length > 0 && <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid var(--border)', borderRadius:8, boxShadow:'var(--shadow-md)', zIndex:20, maxHeight:250, overflowY:'auto' }}>
              {productResults.map(p => <div key={p.id} onClick={() => addProduct(p)} style={{padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid var(--border-light)'}} onMouseEnter={e=>e.currentTarget.style.background='#f8f9fa'} onMouseLeave={e=>e.currentTarget.style.background='#fff'}><div style={{fontWeight:600}}>{p.name}</div><div className="text-xs text-secondary">{p.sku} • ₹{p.purchase_price}</div></div>)}
            </div>}
          </div>
          <button onClick={() => setShowScanner(true)} className="btn btn-accent" style={{ whiteSpace:'nowrap', height:40 }}>📷 Scan</button>
        </div>
        {showScanner && <BarcodeScanner onScan={handleBarcodeScan} onClose={() => setShowScanner(false)} title="Scan Product Barcode" continuous={true} />}
        {items.length > 0 ? <table><thead><tr><th>#</th><th>Product</th><th>HSN</th><th>Qty</th><th>Rate (₹)</th><th>Disc%</th><th>GST%</th><th style={{textAlign:'right'}}>Amount</th><th></th></tr></thead><tbody>
          {items.map((item, idx) => { const taxable = item.quantity * item.unit_price * (1-item.discount_pct/100); return (
            <tr key={idx}><td>{idx+1}</td><td style={{fontWeight:600}}>{item.product_name}<div className="text-xs text-secondary">{item.sku}</div></td><td className="td-mono">{item.hsn_code}</td>
            <td><input type="number" min="1" className="form-input" style={{width:70,padding:'4px 8px'}} value={item.quantity} onChange={e => updateItem(idx,'quantity',Number(e.target.value)||1)} /></td>
            <td><input type="number" step="0.01" className="form-input" style={{width:90,padding:'4px 8px'}} value={item.unit_price} onChange={e => updateItem(idx,'unit_price',Number(e.target.value)||0)} /></td>
            <td><input type="number" className="form-input" style={{width:60,padding:'4px 8px'}} value={item.discount_pct} onChange={e => updateItem(idx,'discount_pct',Number(e.target.value)||0)} /></td>
            <td className="td-mono">{item.gst_rate}%</td>
            <td className="td-amount font-bold">₹{(taxable + taxable*item.gst_rate/100).toFixed(2)}</td>
            <td><button onClick={() => removeItem(idx)} className="btn btn-icon" style={{color:'var(--danger)',background:'transparent'}}>✕</button></td></tr>
          ); })}
        </tbody></table> : <div className="empty-state" style={{padding:32}}><h3>No items</h3><p>Search and add products</p></div>}
      </div></div>
      {items.length > 0 && <div className="flex items-center justify-between mb-4"><div className="font-bold" style={{fontSize:18}}>Estimated Total: ₹{total.toFixed(2)}</div>
        <div className="flex gap-3"><button onClick={handleSubmit} className="btn btn-primary btn-lg" disabled={loading}>{loading ? 'Creating...' : 'Create Purchase Order'}</button></div>
      </div>}
    </div>
  );
}
